/**
 * Classify a tus.onError error as transient (recoverable — should auto-pause
 * so the user can resume when the network is better) or terminal (a real
 * client-side failure that pausing wouldn't help).
 *
 * See bug 2026-08: prior to this classifier, every non-423 tus error was
 * treated as terminal FAILED, producing stranded zombie uploads that the
 * user couldn't see or resume even though the backend still had the row.
 *
 * Rules (matches the answer to the "auto-pause criteria" AskUserQuestion):
 *   - No HTTP response (network error / TCP reset / DNS)     → transient
 *   - 5xx server error                                        → transient
 *   - 408 request timeout                                     → transient
 *   - 401 auth error (token refresh may have failed briefly)  → transient
 *   - 400 / 403 / 404 / 409 / 410                             → terminal
 *   - 423 locked (session takeover)                           → terminal
 *     (the takeover path is handled in its own if-branch before this
 *      classifier is called; returning terminal here is defensive)
 *   - Unknown / undefined                                     → transient
 *     (better to over-pause than to strand — user can always cancel a
 *      paused upload but can't rescue a stranded one)
 *
 * @param {any} err   error object from tus.onError (may be null)
 * @returns {{ transient: boolean, reason: string }}
 */
export const classifyTusError = (err) => {
  const status = safeStatus(err);

  if (status == null) {
    return { transient: true, reason: "network_error" };
  }
  if (status >= 500 && status < 600) {
    return { transient: true, reason: "server_5xx" };
  }
  if (status === 408) {
    return { transient: true, reason: "request_timeout" };
  }
  if (status === 401) {
    return { transient: true, reason: "auth_expired" };
  }
  // 409 for a TUS PATCH is spec-defined as an Upload-Offset conflict — the
  // server's offset drifted from ours (typically because a prior PATCH's
  // bytes landed server-side but we never saw the response, then our next
  // PATCH sent from the pre-flush offset). Recoverable via a HEAD+resume,
  // which tus-js-client handles automatically on retry. Auto-pause here as
  // a safety net if retries eventually give up too — better than a terminal
  // FAILED that discards a resumable upload. See 2026-08-05 incident.
  if (status === 409) {
    return { transient: true, reason: "tus_offset_conflict" };
  }
  // 404 / 410 on a TUS resume mean "the server no longer knows this upload
  // session" — TUS storage was purged, TTL expired, or the server rebound
  // the file to a new URL. Recoverable by falling back to a fresh POST
  // (byte-0 restart) — that's cheaper than leaving the upload stranded
  // forever in FAILED with no user affordance to recover. The resume path
  // in ChunkedUploadService now sets `endpoint` alongside `uploadUrl` so
  // tus-js-client's built-in 4xx-to-POST fallback engages when this fires.
  // See 2026-08-06 incident: paused upload → server-side TUS gone → resume
  // loop, 404-per-click, forever.
  if (status === 404 || status === 410) {
    return { transient: true, reason: "tus_session_gone" };
  }
  // 400 / 403 / 422 / etc. — real client-side errors. Pausing won't help;
  // something about the request itself is wrong.
  if (status >= 400 && status < 500) {
    return { transient: false, reason: `client_${status}` };
  }
  // 2xx / 3xx here would be surprising in an onError callback. Treat as
  // transient so the upload can retry rather than strand.
  return { transient: true, reason: "unknown_error" };
};

const safeStatus = (err) => {
  try {
    const value = err?.originalResponse?.getStatus?.();
    return typeof value === "number" ? value : null;
  } catch (_error) {
    return null;
  }
};
